import React, { useState, useEffect, useCallback } from 'react';
import {
  Sun, Moon, LogOut, Users, LayoutDashboard,
  Settings, HelpCircle, Menu, Mail, History,
  Activity, Clock, AlertTriangle, Check, Shield,
  RefreshCw, Eye, UserCheck, LogIn, LogOut as LogOutIcon,
  XCircle, Lock, Zap, Search, ChevronRight
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

function ActivityPage({ user, onLogout, theme, setTheme, setPage }) {
  const initials = user.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState({ logs: [], total: 0 });
  const [logsPage, setLogsPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [section, setSection] = useState('today');
  const [loading, setLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    try { const r = await api.getActivitySummary(); setSummary(r); } catch {}
  }, []);

  const loadLogs = useCallback(async (page = 1, action = '', status = '') => {
    try {
      const params = { page, limit: 50 };
      if (action) params.action = action;
      if (status) params.status = status;
      const r = await api.getActivityLogs(params);
      setLogs(r);
    } catch {}
  }, []);

  useEffect(() => {
    loadSummary();
    loadLogs(1, '', '');
  }, [loadSummary, loadLogs]);

  const actionLabels = {
    login: 'Login',
    login_2fa_required: '2FA Solicitado',
    logout: 'Logout',
    logout_all: 'Encerrar Sessoes',
    password_reset: 'Redefinir Senha',
  };

  const actionIcons = {
    login: LogIn,
    login_2fa_required: Lock,
    logout: LogOutIcon,
    logout_all: Shield,
    password_reset: Lock,
  };

  const statusConfig = {
    success: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)', label: 'Sucesso' },
    failed: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)', label: 'Falhou' },
    blocked: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: 'Bloqueado' },
  };

  const failReasons = {
    wrong_password: 'Senha incorreta',
    cpf_not_found: 'CPF nao encontrado',
    '2fa_invalid': 'Codigo 2FA invalido',
    rate_limit: 'Muitas tentativas',
    maintenance: 'Sistema em manutencao',
  };

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
              <Users size={19} /><span>Admin</span>
            </a>
            <a href="#emails" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('emails'); }}>
              <Mail size={19} /><span>E-mails</span>
            </a>
            <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
              <Zap size={19} /><span>Automacao IA</span>
            </a>
            <a href="#activity" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <Activity size={19} /><span>Atividade</span>
            </a>
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Historico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configuracoes</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main" style={{ maxWidth: '1100px' }}>
          <div className="page-header">
            <div>
              <p className="page-greeting">Monitoramento</p>
              <h1 className="page-title">Atividade do Sistema</h1>
            </div>
            <button onClick={() => { loadSummary(); loadLogs(logsPage, filter, statusFilter); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '10px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: '0.8rem' }}>
              <RefreshCw size={14} /> Atualizar
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid var(--card-border)' }}>
            {[['today', 'Hoje', Activity], ['all', 'Todos os Logs', Clock]].map(([key, label, Icon]) => (
              <button key={key} onClick={() => { setSection(key); if (key === 'today') loadSummary(); if (key === 'all') loadLogs(1, filter, statusFilter); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px', padding: '12px 20px', border: 'none',
                  background: 'transparent', cursor: 'pointer', fontSize: '0.84rem',
                  fontWeight: section === key ? 600 : 400, color: section === key ? '#3b82f6' : 'var(--text-color)',
                  borderBottom: section === key ? '2px solid #3b82f6' : '2px solid transparent'
                }}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          {/* ======= TODAY SECTION ======= */}
          {section === 'today' && summary && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <LogIn size={14} color='#22c55e' /> Logins Hoje
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#22c55e' }}>{summary.logins?.success || 0}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{summary.logins?.unique_users || 0} usuarios unicos</div>
                </div>
                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <XCircle size={14} color='#ef4444' /> Tentativas Falhas
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ef4444' }}>{summary.logins?.failed || 0}</div>
                </div>
                <div style={{ padding: '20px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Shield size={14} color='#f59e0b' /> Bloqueados
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#f59e0b' }}>{summary.logins?.blocked || 0}</div>
                </div>
              </div>

              {/* Quem acessou hoje */}
              <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserCheck size={18} /> Quem acessou hoje ({summary.recentLogins?.length || 0})
                </h3>
                {summary.recentLogins?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {summary.recentLogins.map((l, i) => (
                      <div key={i} style={{
                        display: 'grid', gridTemplateColumns: '1.5fr 0.8fr 0.8fr 1fr',
                        padding: '10px 14px', borderRadius: '8px', background: 'var(--input-bg)',
                        alignItems: 'center', gap: '8px'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-color)' }}>{l.name}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{l.role}</div>
                        </div>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{l.device}</span>
                        <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{l.ip_address}</span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                          {new Date(l.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center', padding: '20px 0' }}>Nenhum acesso registrado hoje</p>
                )}
              </div>

              {/* Tentativas falhas */}
              {summary.failedAttempts?.length > 0 && (
                <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={18} /> Tentativas de acesso falhas/bloqueadas
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {summary.failedAttempts.map((f, i) => {
                      const reason = f.details?.reason ? failReasons[f.details.reason] || f.details.reason : 'Desconhecido';
                      const sc = statusConfig[f.status] || statusConfig.failed;
                      return (
                        <div key={i} style={{
                          display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.8fr 0.8fr 1fr',
                          padding: '10px 14px', borderRadius: '8px', background: 'var(--input-bg)',
                          alignItems: 'center', gap: '8px'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-color)' }}>
                              {f.user_name || f.details?.cpf || 'Desconhecido'}
                            </div>
                            {f.user_cpf && <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>CPF: {f.user_cpf}</div>}
                          </div>
                          <span style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 500 }}>{reason}</span>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.62rem', fontWeight: 600, background: sc.bg, color: sc.color }}>{sc.label}</span>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{f.ip_address}</span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'right' }}>
                            {new Date(f.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Atividade por hora */}
              {summary.byHour?.length > 0 && (
                <div style={{ padding: '24px', borderRadius: '14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Clock size={18} /> Atividade por Hora
                  </h3>
                  <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: '100px' }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const data = summary.byHour.find(b => b.hour === h);
                      const total = (parseInt(data?.logins) || 0) + (parseInt(data?.failures) || 0);
                      const max = Math.max(...summary.byHour.map(b => (parseInt(b.logins) || 0) + (parseInt(b.failures) || 0)), 1);
                      const pct = total > 0 ? Math.max(8, (total / max) * 100) : 0;
                      const hasFailures = parseInt(data?.failures) > 0;
                      return (
                        <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <div style={{
                            width: '100%', height: `${pct}%`, minHeight: pct > 0 ? '4px' : '0',
                            background: hasFailures ? 'linear-gradient(to top, #ef4444, #22c55e)' : '#22c55e',
                            borderRadius: '3px 3px 0 0', transition: 'height 0.3s'
                          }} title={`${h}h: ${data?.logins || 0} logins, ${data?.failures || 0} falhas`} />
                          <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>{h}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======= ALL LOGS SECTION ======= */}
          {section === 'all' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Filters */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[['', 'Todos'], ['login', 'Login'], ['logout', 'Logout'], ['password_reset', 'Reset Senha']].map(([key, label]) => (
                  <button key={key} onClick={() => { setFilter(key); setLogsPage(1); loadLogs(1, key, statusFilter); }}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '0.76rem', cursor: 'pointer',
                      border: filter === key ? '2px solid #3b82f6' : '1px solid var(--card-border)',
                      background: filter === key ? 'rgba(59,130,246,0.08)' : 'transparent',
                      color: filter === key ? '#3b82f6' : 'var(--text-color)', fontWeight: filter === key ? 600 : 400
                    }}>{label}</button>
                ))}
                <div style={{ width: '1px', background: 'var(--card-border)', margin: '0 4px' }} />
                {[['', 'Todos'], ['success', 'Sucesso'], ['failed', 'Falha'], ['blocked', 'Bloqueado']].map(([key, label]) => (
                  <button key={`s-${key}`} onClick={() => { setStatusFilter(key); setLogsPage(1); loadLogs(1, filter, key); }}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', fontSize: '0.76rem', cursor: 'pointer',
                      border: statusFilter === key ? '2px solid #8b5cf6' : '1px solid var(--card-border)',
                      background: statusFilter === key ? 'rgba(139,92,246,0.08)' : 'transparent',
                      color: statusFilter === key ? '#8b5cf6' : 'var(--text-color)', fontWeight: statusFilter === key ? 600 : 400
                    }}>{label}</button>
                ))}
              </div>

              {/* Table */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 0.8fr 0.8fr 1.2fr',
                  padding: '12px 18px', fontSize: '0.72rem', fontWeight: 600,
                  color: 'var(--text-muted)', borderBottom: '1px solid var(--card-border)',
                  textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  <span>Usuario</span>
                  <span>Acao</span>
                  <span>Status</span>
                  <span>Dispositivo</span>
                  <span>IP</span>
                  <span>Data/Hora</span>
                </div>

                {logs.logs.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Nenhum registro</div>
                ) : logs.logs.map((log, i) => {
                  const sc = statusConfig[log.status] || statusConfig.success;
                  const ActionIcon = actionIcons[log.action] || Activity;
                  return (
                    <div key={log.id} style={{
                      display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.8fr 0.8fr 0.8fr 1.2fr',
                      padding: '10px 18px', alignItems: 'center',
                      borderBottom: i < logs.logs.length - 1 ? '1px solid var(--card-border)' : 'none'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-color)' }}>
                          {log.user_name || log.details?.cpf || '—'}
                        </div>
                        <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>{log.user_role || ''}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem', color: 'var(--text-color)' }}>
                        <ActionIcon size={13} /> {actionLabels[log.action] || log.action}
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.62rem', fontWeight: 600, background: sc.bg, color: sc.color, width: 'fit-content' }}>
                        {sc.label}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{log.device}</span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{log.ip_address}</span>
                      <div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                        {log.details?.reason && (
                          <div style={{ fontSize: '0.6rem', color: '#ef4444' }}>{failReasons[log.details.reason] || log.details.reason}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {logs.total > 50 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <button onClick={() => { const p = Math.max(1, logsPage - 1); setLogsPage(p); loadLogs(p, filter, statusFilter); }}
                    disabled={logsPage === 1}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    Anterior
                  </button>
                  <span style={{ padding: '6px 14px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {logsPage} / {Math.ceil(logs.total / 50)}
                  </span>
                  <button onClick={() => { const p = logsPage + 1; setLogsPage(p); loadLogs(p, filter, statusFilter); }}
                    disabled={logsPage >= Math.ceil(logs.total / 50)}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    Proximo
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default ActivityPage;
